import { cn } from "@/lib/utils.ts";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
import * as React from "react";

interface CheckboxCardProps extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  label: string;
  description?: string;
  icon?: React.ReactNode;
  showIcon?: boolean;
  showDescription?: boolean;
  className?: string;
  labelClassName?: string;
  /** Classes for the description. Pass `whitespace-normal` to let long copy
   *  wrap instead of truncating to one line. */
  descriptionClassName?: string;
  variant?: "checkbox" | "radio";
}

const CheckboxCard = React.forwardRef<
  React.ComponentRef<typeof CheckboxPrimitive.Root>,
  CheckboxCardProps
>(
  (
    {
      label,
      description,
      icon,
      showIcon = true,
      showDescription = true,
      className,
      labelClassName,
      descriptionClassName,
      disabled,
      checked,
      onCheckedChange,
      variant = "checkbox",
      ...props
    },
    ref,
  ) => {
    const isActive = checked === true || checked === "indeterminate";
    const isRadio = variant === "radio";

    return (
      <div
        className={cn(
          "flex gap-3 items-start justify-start p-4 rounded-xl cursor-pointer transition-colors",
          "bg-white border border-zinc-200",
          !disabled && "hover:bg-[#edebe7]",
          isActive && "border-primary",
          disabled && "cursor-not-allowed opacity-60",
          className,
        )}
        onClick={(e) => {
          if (disabled) return;
          // Inside a <form>, Radix renders a hidden input alongside the checkbox
          // and dispatches a bubbling click on it every time `checked` changes,
          // to keep native form state in sync. That click lands here, and
          // toggling on it would flip the value straight back — which fires the
          // next synthetic click, and so on until React gives up ("Maximum
          // update depth exceeded"). Only a click on the card itself is a user
          // choosing this option.
          if (e.target instanceof HTMLInputElement) return;
          onCheckedChange?.(!checked);
        }}
      >
        {showIcon && (
          <div className="bg-white rounded-full shrink-0 border border-zinc-200 shadow-sm">
            <div className="flex gap-2.5 items-center justify-center p-2.5">
              {icon || <div className="size-5 text-zinc-500">{icon}</div>}
            </div>
          </div>
        )}

        <div className="flex-1 flex flex-col gap-1 min-w-0 overflow-hidden">
          <div className="flex gap-1 items-center min-w-0">
            <span
              className={cn(
                isRadio ? "font-medium" : "font-normal",
                "text-sm leading-5 truncate",
                !labelClassName &&
                  (disabled ? "text-zinc-500" : isRadio ? "text-zinc-900" : "text-zinc-950"),
                labelClassName,
              )}
              title={label}
            >
              {label}
            </span>
          </div>
          {showDescription && description && (
            <span
              className={cn(
                "text-xs leading-4 truncate",
                disabled ? "text-zinc-400" : "text-[#64646c]",
                descriptionClassName,
              )}
              title={description}
            >
              {description}
            </span>
          )}
        </div>

        <CheckboxPrimitive.Root
          ref={ref}
          className={cn(
            "peer size-5 shrink-0 rounded-full border ring-offset-background",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed",
            isActive
              ? "bg-primary border-primary text-primary-foreground"
              : disabled
                ? "bg-zinc-100 border-zinc-200"
                : "bg-white border-zinc-200 hover:border-zinc-300",
          )}
          disabled={disabled}
          checked={checked}
          onCheckedChange={onCheckedChange}
          onClick={(e) => e.stopPropagation()}
          {...props}
        >
          <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
            {isRadio ? (
              <div className="size-2.5 rounded-full bg-white" />
            ) : (
              <Check className="size-3" />
            )}
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
      </div>
    );
  },
);

CheckboxCard.displayName = "CheckboxCard";

export { CheckboxCard };
